import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
    Users, 
    MessageSquare, 
    Sparkles, 
    TrendingUp, 
    Bot, 
    Zap,
    Clock,
    ArrowUpRight,
    ArrowDownRight,
    Activity,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useTranslation } from "react-i18next";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { 
    AreaChart, 
    Area, 
    XAxis, 
    YAxis, 
    Tooltip, 
    ResponsiveContainer,
    BarChart,
    Bar,
    CartesianGrid,
    Legend,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

type TimeRange = "24h" | "7d" | "30d";

const formatNumber = (num: number) => {
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return num.toString();
};

const formatDate = (dateStr: string, range: TimeRange) => {
    const date = new Date(dateStr);
    if (range === "24h") {
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const StatCard = ({ 
    title, 
    value, 
    description, 
    icon: Icon, 
    trend,
    trendValue,
    loading 
}: { 
    title: string; 
    value: string | number; 
    description?: string; 
    icon: React.ElementType;
    trend?: "up" | "down" | "neutral";
    trendValue?: string;
    loading?: boolean;
}) => {
    if (loading) {
        return (
            <Card className="relative overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-8 rounded" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-8 w-20 mb-1" />
                    <Skeleton className="h-3 w-32" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
                <div className="p-2 rounded-lg bg-primary/10">
                    <Icon className="h-4 w-4 text-primary" />
                </div>
            </CardHeader>
            <CardContent>
                <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{value}</div>
                    {trend && trendValue && (
                        <Badge 
                            variant="outline" 
                            className={`text-xs ${
                                trend === "up" 
                                    ? "text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-800" 
                                    : trend === "down"
                                    ? "text-red-600 border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800"
                                    : "text-muted-foreground"
                            }`}
                        >
                            {trend === "up" ? (
                                <ArrowUpRight className="h-3 w-3 mr-0.5" />
                            ) : trend === "down" ? (
                                <ArrowDownRight className="h-3 w-3 mr-0.5" />
                            ) : null}
                            {trendValue}
                        </Badge>
                    )}
                </div>
                {description && (
                    <p className="text-xs text-muted-foreground mt-1">{description}</p>
                )}
            </CardContent>
        </Card>
    );
};

const TokenUsageChart = ({ 
    data, 
    range, 
    loading 
}: { 
    data: any[]; 
    range: TimeRange;
    loading: boolean;
}) => {
    const { t } = useTranslation();

    if (loading) {
        return (
            <Card className="col-span-full">
                <CardHeader>
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-[300px] w-full" />
                </CardContent>
            </Card>
        );
    }

    const chartData = data.map((item) => ({
        name: formatDate(item.date, range),
        input: Number(item.inputTokens) || 0,
        output: Number(item.outputTokens) || 0,
        reasoning: Number(item.reasoningTokens) || 0,
        cached: Number(item.cachedTokens) || 0,
    }));

    // Check if we have enough data points
    const hasEnoughData = chartData.length >= 2;

    return (
        <Card className="col-span-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-500" />
                    {t("dashboard.ai.tokenUsage")}
                </CardTitle>
                <CardDescription>{t("dashboard.ai.tokenUsageDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
                {!hasEnoughData ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        <div className="text-center space-y-2">
                            <Zap className="h-12 w-12 mx-auto opacity-20" />
                            <p className="text-sm font-medium">{t("dashboard.ai.notEnoughData")}</p>
                            <p className="text-xs">{t("dashboard.ai.notEnoughDataDesc")}</p>
                        </div>
                    </div>
                ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorInput" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorOutput" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorReasoning" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                            dataKey="name" 
                            stroke="#888888" 
                            fontSize={12} 
                            tickLine={false}
                            axisLine={false}
                        />
                        <YAxis 
                            stroke="#888888" 
                            fontSize={12} 
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(value) => formatNumber(value)}
                        />
                        <Tooltip
                            content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                    return (
                                        <div className="rounded-lg border bg-background p-3 shadow-lg">
                                            <p className="font-semibold mb-2">{label}</p>
                                            <div className="space-y-1">
                                                {payload.map((entry, index) => (
                                                    <div key={index} className="flex items-center justify-between gap-4 text-sm">
                                                        <span className="flex items-center gap-2">
                                                            <span 
                                                                className="h-2 w-2 rounded-full" 
                                                                style={{ backgroundColor: entry.color }}
                                                            />
                                                            {entry.name}
                                                        </span>
                                                        <span className="font-mono font-medium">
                                                            {formatNumber(Number(entry.value))}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <Legend />
                        <Area
                            type="monotone"
                            dataKey="input"
                            name={t("dashboard.ai.inputTokens")}
                            stroke="hsl(var(--primary))"
                            fillOpacity={1}
                            fill="url(#colorInput)"
                            strokeWidth={2}
                        />
                        <Area
                            type="monotone"
                            dataKey="output"
                            name={t("dashboard.ai.outputTokens")}
                            stroke="#10b981"
                            fillOpacity={1}
                            fill="url(#colorOutput)"
                            strokeWidth={2}
                        />
                        <Area
                            type="monotone"
                            dataKey="reasoning"
                            name={t("dashboard.ai.reasoningTokens")}
                            stroke="#f59e0b"
                            fillOpacity={1}
                            fill="url(#colorReasoning)"
                            strokeWidth={2}
                        />
                    </AreaChart>
                </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

const MessagesChart = ({ 
    data, 
    range,
    loading 
}: { 
    data: any[]; 
    range: TimeRange;
    loading: boolean;
}) => {
    const { t } = useTranslation();

    if (loading) {
        return (
            <Card>
                <CardHeader>
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-[250px] w-full" />
                </CardContent>
            </Card>
        );
    }

    const chartData = data.map((item) => ({
        name: formatDate(item.date, range),
        user: Number(item.userMessages) || 0,
        assistant: Number(item.assistantMessages) || 0,
    }));

    // Check if we have enough data points
    const hasEnoughData = chartData.length >= 2;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-blue-500" />
                    {t("dashboard.ai.messageActivity")}
                </CardTitle>
                <CardDescription>{t("dashboard.ai.messageActivityDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
                {!hasEnoughData ? (
                    <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                        <div className="text-center space-y-2">
                            <MessageSquare className="h-12 w-12 mx-auto opacity-20" />
                            <p className="text-sm font-medium">{t("dashboard.ai.notEnoughData")}</p>
                            <p className="text-xs">{t("dashboard.ai.notEnoughDataDesc")}</p>
                        </div>
                    </div>
                ) : (
                <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                            dataKey="name" 
                            stroke="#888888" 
                            fontSize={12} 
                            tickLine={false}
                            axisLine={false}
                        />
                        <YAxis 
                            stroke="#888888" 
                            fontSize={12} 
                            tickLine={false}
                            axisLine={false}
                        />
                        <Tooltip
                            content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                    return (
                                        <div className="rounded-lg border bg-background p-3 shadow-lg">
                                            <p className="font-semibold mb-2">{label}</p>
                                            <div className="space-y-1">
                                                {payload.map((entry, index) => (
                                                    <div key={index} className="flex items-center justify-between gap-4 text-sm">
                                                        <span className="flex items-center gap-2">
                                                            <span 
                                                                className="h-2 w-2 rounded-full" 
                                                                style={{ backgroundColor: entry.color }}
                                                            />
                                                            {entry.name}
                                                        </span>
                                                        <span className="font-mono font-medium">{entry.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <Legend />
                        <Bar 
                            dataKey="user" 
                            name={t("dashboard.ai.userMessages")} 
                            fill="hsl(var(--primary))" 
                            radius={[4, 4, 0, 0]}
                        />
                        <Bar 
                            dataKey="assistant" 
                            name={t("dashboard.ai.assistantMessages")} 
                            fill="#10b981" 
                            radius={[4, 4, 0, 0]}
                        />
                    </BarChart>
                </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

const ResponseTimeChart = ({ 
    data, 
    range, 
    loading 
}: { 
    data: any[]; 
    range: TimeRange;
    loading: boolean;
}) => {
    const { t } = useTranslation();

    if (loading) {
        return (
            <Card className="col-span-full">
                <CardHeader>
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-[250px] w-full" />
                </CardContent>
            </Card>
        );
    }

    const chartData = data.map((item) => ({
        name: formatDate(item.date, range),
        avg: Number(item.avgResponseTime) / 1000 || 0, // Convert to seconds
        min: Number(item.minResponseTime) / 1000 || 0,
        max: Number(item.maxResponseTime) / 1000 || 0,
    }));

    // Check if we have enough data points
    const hasEnoughData = chartData.length >= 2;

    return (
        <Card className="col-span-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-purple-500" />
                    {t("dashboard.ai.responseTime")}
                </CardTitle>
                <CardDescription>{t("dashboard.ai.responseTimeDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
                {!hasEnoughData ? (
                    <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                        <div className="text-center space-y-2">
                            <Activity className="h-12 w-12 mx-auto opacity-20" />
                            <p className="text-sm font-medium">{t("dashboard.ai.notEnoughData")}</p>
                            <p className="text-xs">{t("dashboard.ai.notEnoughDataDesc")}</p>
                        </div>
                    </div>
                ) : (
                <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorResponseTime" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                            dataKey="name" 
                            stroke="#888888" 
                            fontSize={12} 
                            tickLine={false}
                            axisLine={false}
                        />
                        <YAxis 
                            stroke="#888888" 
                            fontSize={12} 
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(value) => `${value.toFixed(1)}s`}
                        />
                        <Tooltip
                            content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                    return (
                                        <div className="rounded-lg border bg-background p-3 shadow-lg">
                                            <p className="font-semibold mb-2">{label}</p>
                                            <div className="space-y-1">
                                                <div className="flex items-center justify-between gap-4 text-sm">
                                                    <span className="text-muted-foreground">
                                                        {t("dashboard.ai.average")}
                                                    </span>
                                                    <span className="font-mono font-medium">
                                                        {Number(payload[0]?.value).toFixed(2)}s
                                                    </span>
                                                </div>
                                                {payload[1] && (
                                                    <>
                                                        <div className="flex items-center justify-between gap-4 text-sm">
                                                            <span className="text-muted-foreground">
                                                                {t("dashboard.ai.minimum")}
                                                            </span>
                                                            <span className="font-mono font-medium text-emerald-600 dark:text-emerald-400">
                                                                {Number(payload[1]?.value).toFixed(2)}s
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-4 text-sm">
                                                            <span className="text-muted-foreground">
                                                                {t("dashboard.ai.maximum")}
                                                            </span>
                                                            <span className="font-mono font-medium text-red-600 dark:text-red-400">
                                                                {Number(payload[2]?.value).toFixed(2)}s
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <Legend />
                        <Area
                            type="monotone"
                            dataKey="avg"
                            name={t("dashboard.ai.average")}
                            stroke="#a855f7"
                            fillOpacity={1}
                            fill="url(#colorResponseTime)"
                            strokeWidth={2}
                        />
                        <Area
                            type="monotone"
                            dataKey="min"
                            name={t("dashboard.ai.minimum")}
                            stroke="#10b981"
                            fillOpacity={0}
                            strokeWidth={1}
                            strokeDasharray="3 3"
                        />
                        <Area
                            type="monotone"
                            dataKey="max"
                            name={t("dashboard.ai.maximum")}
                            stroke="#ef4444"
                            fillOpacity={0}
                            strokeWidth={1}
                            strokeDasharray="3 3"
                        />
                    </AreaChart>
                </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

const UsageInsightsCard = ({ 
    totalUsers,
    activeUsers,
    totalMessages,
    totalThreads,
    loading 
}: { 
    totalUsers: number;
    activeUsers: number;
    totalMessages: number;
    totalThreads: number;
    loading: boolean;
}) => {
    const { t } = useTranslation();

    const engagementRate = totalUsers > 0 ? ((activeUsers / totalUsers) * 100) : 0;
    const avgMessagesPerConversation = totalThreads > 0 ? (totalMessages / totalThreads) : 0;
    const avgMessagesPerUser = activeUsers > 0 ? (totalMessages / activeUsers) : 0;

    if (loading) {
        return (
            <Card>
                <CardHeader>
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-[200px] w-full" />
                </CardContent>
            </Card>
        );
    }

    const insights = [
        {
            label: t("dashboard.ai.engagementRate"),
            value: engagementRate.toFixed(1),
            suffix: "%",
            description: t("dashboard.ai.engagementRateDesc"),
            color: "text-emerald-500",
        },
        {
            label: t("dashboard.ai.avgMessagesPerConversation"),
            value: avgMessagesPerConversation.toFixed(1),
            suffix: "",
            description: t("dashboard.ai.avgMessagesPerConversationDesc"),
            color: "text-blue-500",
        },
        {
            label: t("dashboard.ai.avgMessagesPerUser"),
            value: avgMessagesPerUser.toFixed(1),
            suffix: "",
            description: t("dashboard.ai.avgMessagesPerUserDesc"),
            color: "text-violet-500",
        },
    ];

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-500" />
                    {t("dashboard.ai.usageInsights")}
                </CardTitle>
                <CardDescription>{t("dashboard.ai.usageInsightsDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {insights.map((insight, index) => (
                    <div 
                        key={index} 
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                        <div className="space-y-0.5">
                            <p className="text-sm font-medium">{insight.label}</p>
                            <p className="text-xs text-muted-foreground">{insight.description}</p>
                        </div>
                        <span className={`text-2xl font-bold font-mono ${insight.color}`}>
                            {insight.value}{insight.suffix}
                        </span>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

const TopUsersCard = ({ 
    data, 
    loading 
}: { 
    data: { userId: string | null; userName: string | null; userEmail: string | null; messageCount: number }[];
    loading: boolean;
}) => {
    const { t } = useTranslation();

    if (loading) {
        return (
            <Card>
                <CardHeader>
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <Skeleton className="h-9 w-9 rounded-full" />
                                <div className="flex-1">
                                    <Skeleton className="h-4 w-24 mb-1" />
                                    <Skeleton className="h-3 w-32" />
                                </div>
                                <Skeleton className="h-6 w-12" />
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-purple-500" />
                    {t("dashboard.ai.topUsers")}
                </CardTitle>
                <CardDescription>{t("dashboard.ai.topUsersDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {data.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">
                            {t("dashboard.ai.noActivity")}
                        </p>
                    ) : (
                        data.map((user, index) => (
                            <div key={user.userId || index} className="flex items-center gap-3">
                                <div className="relative">
                                    <Avatar className="h-9 w-9">
                                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                                            {user.userName?.charAt(0)?.toUpperCase() || "?"}
                                        </AvatarFallback>
                                    </Avatar>
                                    {index < 3 && (
                                        <span className={`absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${
                                            index === 0 
                                                ? "bg-amber-500 text-white" 
                                                : index === 1 
                                                ? "bg-gray-400 text-white" 
                                                : "bg-amber-700 text-white"
                                        }`}>
                                            {index + 1}
                                        </span>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">
                                        {user.userName || t("dashboard.ai.unknownUser")}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {user.userEmail || "-"}
                                    </p>
                                </div>
                                <Badge variant="secondary" className="font-mono">
                                    {user.messageCount}
                                </Badge>
                            </div>
                        ))
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

const AgentsCard = ({ 
    data, 
    loading 
}: { 
    data: { totalAgents: number; enabledAgents: number; agents: { id: string; name: string; type: string; enabled: boolean }[] } | undefined;
    loading: boolean;
}) => {
    const { t } = useTranslation();

    if (loading || !data) {
        return (
            <Card>
                <CardHeader>
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-[200px] w-full" />
                </CardContent>
            </Card>
        );
    }

    // Filter to only show enabled agents
    const enabledAgents = data.agents.filter((agent) => agent.enabled);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-indigo-500" />
                    {t("dashboard.ai.agents")}
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                    <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    {data.enabledAgents} {t("dashboard.ai.agentsActive")}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {enabledAgents.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">
                            {t("dashboard.ai.noAgentsEnabled")}
                        </p>
                    ) : (
                        <>
                            {enabledAgents.slice(0, 6).map((agent) => (
                                <div key={agent.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                                    <div className="flex items-center gap-2">
                                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                                        <span className="text-sm font-medium">{agent.name}</span>
                                    </div>
                                    <Badge variant="outline" className="text-xs">
                                        {agent.type}
                                    </Badge>
                                </div>
                            ))}
                            {enabledAgents.length > 6 && (
                                <p className="text-xs text-muted-foreground text-center pt-2">
                                    +{enabledAgents.length - 6} {t("dashboard.ai.moreAgents")}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default function AIDashboard() {
    const { t } = useTranslation();
    const [timeRange, setTimeRange] = useState<TimeRange>("7d");

    const { data: stats, isLoading } = trpc.dashboard.getStats.useQuery({ range: timeRange });

    const totalTokens = stats?.tokens?.totals;
    const userMessagesCount = stats?.messages?.byRole?.find((r) => r.role === "user")?.count || 0;
    const assistantMessagesCount = stats?.messages?.byRole?.find((r) => r.role === "assistant")?.count || 0;

    return (
        <div className="space-y-6">
            {/* Time Range Selector */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">{t("dashboard.ai.title")}</h2>
                    <p className="text-sm text-muted-foreground">{t("dashboard.ai.subtitle")}</p>
                </div>
                <Select value={timeRange} onValueChange={(value: TimeRange) => setTimeRange(value)}>
                    <SelectTrigger className="w-[140px]">
                        <Clock className="h-4 w-4 mr-2" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="24h">{t("dashboard.ai.last24h")}</SelectItem>
                        <SelectItem value="7d">{t("dashboard.ai.last7d")}</SelectItem>
                        <SelectItem value="30d">{t("dashboard.ai.last30d")}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Key Metrics */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title={t("dashboard.ai.totalUsers")}
                    value={stats?.users?.total || 0}
                    description={t("dashboard.ai.registeredUsers")}
                    icon={Users}
                    loading={isLoading}
                />
                <StatCard
                    title={t("dashboard.ai.activeUsers")}
                    value={stats?.users?.active || 0}
                    description={t("dashboard.ai.activeUsersDesc", { range: timeRange })}
                    icon={Sparkles}
                    loading={isLoading}
                />
                <StatCard
                    title={t("dashboard.ai.conversations")}
                    value={stats?.threads || 0}
                    description={t("dashboard.ai.conversationsDesc")}
                    icon={MessageSquare}
                    loading={isLoading}
                />
                <StatCard
                    title={t("dashboard.ai.totalMessages")}
                    value={stats?.messages?.total || 0}
                    description={`${userMessagesCount} ${t("dashboard.ai.user")} / ${assistantMessagesCount} ${t("dashboard.ai.assistant")}`}
                    icon={TrendingUp}
                    loading={isLoading}
                />
            </div>

            {/* Token Stats & Performance Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <StatCard
                    title={t("dashboard.ai.inputTokens")}
                    value={formatNumber(Number(totalTokens?.inputTokens) || 0)}
                    icon={Zap}
                    loading={isLoading}
                />
                <StatCard
                    title={t("dashboard.ai.outputTokens")}
                    value={formatNumber(Number(totalTokens?.outputTokens) || 0)}
                    icon={Zap}
                    loading={isLoading}
                />
                <StatCard
                    title={t("dashboard.ai.reasoningTokens")}
                    value={formatNumber(Number(totalTokens?.reasoningTokens) || 0)}
                    icon={Sparkles}
                    loading={isLoading}
                />
                <StatCard
                    title={t("dashboard.ai.cachedTokens")}
                    value={formatNumber(Number(totalTokens?.cachedTokens) || 0)}
                    description={t("dashboard.ai.cachedTokensDesc")}
                    icon={Clock}
                    loading={isLoading}
                />
                <StatCard
                    title={t("dashboard.ai.avgResponseTime")}
                    value={stats?.responseTime?.average ? `${(stats.responseTime.average / 1000).toFixed(1)}s` : "N/A"}
                    description={stats?.responseTime?.count ? `${stats.responseTime.count} ${t("dashboard.ai.responses")}` : undefined}
                    icon={Activity}
                    loading={isLoading}
                />
            </div>

            {/* Token Usage Chart */}
            <TokenUsageChart 
                data={stats?.tokens?.usage || []} 
                range={timeRange}
                loading={isLoading}
            />

            {/* Response Time Chart */}
            <ResponseTimeChart 
                data={stats?.responseTimeOverTime || []} 
                range={timeRange}
                loading={isLoading}
            />

            {/* Bottom Row */}
            <div className="grid gap-4 lg:grid-cols-2">
                <MessagesChart 
                    data={stats?.messagesOverTime || []} 
                    range={timeRange}
                    loading={isLoading}
                />
                <UsageInsightsCard 
                    totalUsers={stats?.users?.total || 0}
                    activeUsers={stats?.users?.active || 0}
                    totalMessages={stats?.messages?.total || 0}
                    totalThreads={stats?.threads || 0}
                    loading={isLoading}
                />
            </div>

            {/* Users and Agents */}
            <div className="grid gap-4 lg:grid-cols-2">
                <TopUsersCard 
                    data={stats?.topUsers || []}
                    loading={isLoading}
                />
                <AgentsCard 
                    data={stats?.agents}
                    loading={isLoading}
                />
            </div>
        </div>
    );
}

